<template>
    <div class="ostende-agenda">
        <slot :loading="loading || !ready" />

        <template v-if="ready">
            <div class="mb-3 mb-lg-5">
                <Filters
                    :value="value"
                    :criterias="criterias"
                    :companies="companies"
                    @input="handleValueChanged"
                    :key="hydratingKey"
                />
            </div>

            <div>
                <PerformanceList
                    :performances="results"
                    :audiences="criterias.audience"
                    sidenav
                    list-class="mx-n3"
                />
            </div>

            <b-modal
                modal-class="ostende-search__filter-modal"
                body-class="px-0"
                :visible="modalVisible"
                hide-footer
                @close="handleModalClosed"
                @hide="handleModalHide"
            >
                <template v-slot:modal-header-close>
                    <svg class="d-block" width="15" height="15">
                        <use xlink:href="#sprite-close"></use>
                    </svg>
                </template>
                <router-view
                    :value="value"
                    :criterias="criterias"
                    :companies="companies"
                    @input="handleValueChanged"
                    @companies-change="handleCompaniesChanged"
                    @submit="handleFilterSubmitted"
                />
            </b-modal>
        </template>
        <template v-if="loading">
            <div class="spinner-grow text-primary"></div>
        </template>
    </div>
</template>

<script>
    import axios from "axios";
    import isEqual from "lodash/isEqual";
    import Filters from "./Filters";
    import PerformanceList from "./PerformanceList";
    import router from './router';
    import { serializeQuery, parseQuery } from './util/query';
    import { getCompanies, getCalendar } from "./api";
    import { queriesEquals } from "../search/util/query";

    function isFilterRoute(route) {
        return route && route?.name !== 'home';
    }

    export default {
        components:{
            Filters,
            PerformanceList,
        },
        props: {
            criterias: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                ready: false,
                loading: false,

                value: null,
                results: null,
                error: null,
                hydratingKey: 0,

                companies: null,
            }
        },
        computed: {
            modalVisible() {
                return isFilterRoute(this.$route);
            },
        },
        methods: {
            handleValueChanged(value) {
                if(isFilterRoute(this.$route)) {
                    this.value = value;
                } else {
                    this.$router.push({
                        query: {
                            ...serializeQuery(value)
                        }
                    });
                }
            },
            handleCompaniesChanged(companies) {
                console.log('companies changed');
                this.companies = [...companies];
                this.value = {
                    ...this.value,
                    companies: companies.map(company => company.id),
                }
            },
            handleFilterSubmitted() {
                this.submitFilter();
            },
            handleModalClosed() {
                this.submitFilter();
            },
            handleModalHide(e) {
                if(e.trigger === 'backdrop') {
                    this.submitFilter();
                }
            },
            submitFilter() {
                this.$router.replace({
                    name: 'home',
                    query: this.$route.query,
                });
                if(!queriesEquals(this.$route.query, serializeQuery(this.value))) {
                    this.$router.push({
                        query: serializeQuery(this.value),
                    });
                }
            },
            search(data) {
                this.error = null;
                const stalledTimeout = setTimeout(() => this.loading = true, 500);
                if(this.cancelSearch) {
                    this.cancelSearch();
                }
                return getCalendar(data, {
                    cancelToken: new axios.CancelToken(c => this.cancelSearch = c),
                })
                .then(data => {
                    const { results, search } = data;
                    this.results = results;
                    this.value = search;
                    this.$emit('results');
                    this.loading = false;
                    clearTimeout(stalledTimeout)
                })
                .catch(error => {
                    if(axios.isCancel(error)) {
                        return;
                    }
                    this.loading = false;
                    clearTimeout(stalledTimeout);
                    this.error = 'Une erreur est survenue lors de la recherche';
                    return Promise.reject(error);
                });
            },
            init(route, oldRoute) {
                if(route && oldRoute && isEqual(route.query, oldRoute.query)) {
                    return;
                }
                const query = parseQuery(this.$route.query);
                const value = {
                    ...query,
                };
                this.companies = (this.companies ?? []).filter(company => query.companies.includes(company.id));
                const hasDirtyCompanies = query.companies.some(id =>
                    !(this.companies ?? []).some(company => company.id === id)
                );
                if(hasDirtyCompanies) {
                    getCompanies({ ids:query.companies })
                        .then(results => {
                            this.companies = results;
                        });
                }
                return this.search(value);
            },
            setup() {
                return this.init()
                    .then(() => {
                        this.$router.replace({
                            name: 'home',
                            query: serializeQuery(this.value),
                        }).catch(() => {
                            // catch when route is same
                        });
                        this.$watch('$route', this.init);
                        this.$emit('ready');
                        this.ready = true;
                    })
                    .catch(() => {
                        // if error occurred with current query: redirect to /
                        if(location.search) {
                            this.$router.replace({ name:'home' });
                            this.setup();
                        }
                    });
            },
        },
        created() {
            this.setup();
        },
        mounted() {
            window.addEventListener('popstate', this.onPopState = () => {
                // force refresh when go to previous page (ios filters empty bug)
                this.hydratingKey++;
                // refresh after results received to sync current filters state
                this.$once('results', async () => {
                    await this.$nextTick();
                    this.hydratingKey++;
                });
            });
        },
        destroyed() {
            window.removeEventListener('popstate', this.onPopState);
        },
        router,
    }
</script>